import React, { memo, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { generateKey } from "../../../sharedfiles/helper";
import { tabManager } from "../../sidebar/sidebarSlice";
import { setTableShouldView } from "../../Reports/reportSlice";
import { getPlayer, setIsPlayerExist } from "../../playerTable/playerSearchTableSlice";
import { X } from "lucide-react";
import { cn } from "../../../lib/utils";

// Extracted outside NavigationTabs + wrapped in memo so only the 2 tabs whose
// isActive boolean changed (old active → false, new active → true) re-render on
// each navigation, instead of re-rendering every open tab.
const TabItem = memo(({ item, isActive, onDelete, onChangeTab }) => {
  const handleMouseDown = (e) => {
    if (e.button === 1) {
      e.preventDefault();
      onDelete(item, !!item.isSubItem);
    }
  };
  return (
    <div
      data-active={isActive || undefined}
      className={cn(
        "group inline-flex items-center gap-1.5 whitespace-nowrap rounded-t-md px-3 py-2 text-[13px] font-medium transition-all duration-150 cursor-pointer select-none shrink-0 relative",
        isActive
          ? "bg-primary text-primary-foreground shadow-sm after:absolute after:bottom-[-5px] after:left-0 after:right-0 after:h-[2px] after:bg-primary after:rounded-t-full"
          : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent hover:border-border"
      )}
      onMouseDown={handleMouseDown}
    >
      <Link
        to={item.path}
        onClick={() => onChangeTab(item)}
        className='no-underline text-inherit leading-none'
      >
        {item.subtitle}
      </Link>
      <button
        className={cn(
          "flex items-center justify-center h-4 w-4 rounded-full transition-all duration-100 shrink-0",
          isActive
            ? "hover:bg-primary-foreground/20 text-primary-foreground/70 hover:text-primary-foreground"
            : "opacity-0 group-hover:opacity-100 hover:bg-destructive/15 hover:text-destructive text-muted-foreground"
        )}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete(item, !!item.isSubItem);
        }}
      >
        <X className='h-2.5 w-2.5' />
      </button>
    </div>
  );
});

function NavigationTabs(props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const sidebarTabs = useSelector((st) => st.sidebar.sidebarTabs);
  const tableShouldView = useSelector((st) => st.reports.tableShouldView);
  const tabStack = useSelector((st) => st.sidebar.tabStack);
  const activeTab = useSelector((st) => st.sidebar.activeTab);
  const tabBarRef = useRef(null);

  // Publishes the tab bar's real rendered height (which grows/shrinks as tabs
  // wrap to more or fewer rows) as a CSS var, so any page-level sticky element
  // below it (e.g. DashboardV2's date-filter bar) can offset against the
  // actual height instead of a guessed pixel value.
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;

    const updateOffset = () => {
      document.documentElement.style.setProperty("--sticky-tabs-offset", `${64 + el.offsetHeight}px`);
    };

    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  // Desktop: let the mouse wheel drive horizontal scroll (native wheel scroll
  // is vertical-only; a plain scroll container ignores it otherwise). Needs a
  // real DOM listener with { passive: false } since preventDefault() is a
  // no-op on React's synthetic (passive-by-default) onWheel.
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      if (e.deltaY === 0) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  // Desktop: click-and-drag (cursor grab) horizontal scrolling. A plain
  // overflow-x-auto div only scrolls via its scrollbar/wheel/trackpad — it
  // doesn't let you grab the strip itself and drag it, so that's added here.
  // A small pixel threshold distinguishes an actual drag from a plain click,
  // so tab navigation (the <Link>) still works normally when not dragging.
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;

    let isDown = false;
    let didDrag = false;
    let startX = 0;
    let startScrollLeft = 0;

    const handleMouseDown = (e) => {
      if (e.button !== 0) return; // left click only
      isDown = true;
      didDrag = false;
      startX = e.pageX;
      startScrollLeft = el.scrollLeft;
    };

    const handleMouseMove = (e) => {
      if (!isDown) return;
      const delta = e.pageX - startX;
      if (Math.abs(delta) > 4) {
        didDrag = true;
        el.scrollLeft = startScrollLeft - delta;
      }
    };

    const endDrag = () => {
      isDown = false;
    };

    // Only swallow the click when a real drag happened, so switching tabs
    // via a normal click is unaffected.
    const handleClickCapture = (e) => {
      if (didDrag) {
        e.preventDefault();
        e.stopPropagation();
        didDrag = false;
      }
    };

    el.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", endDrag);
    el.addEventListener("click", handleClickCapture, true);

    return () => {
      el.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", endDrag);
      el.removeEventListener("click", handleClickCapture, true);
    };
  }, []);

  // Whenever the active tab changes (click, or navigation elsewhere setting
  // it), bring it into the center of the scrollable strip.
  useEffect(() => {
    const el = tabBarRef.current;
    if (!el) return;

    const activeEl = el.querySelector('[data-active="true"]');
    activeEl?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeTab]);

  // Stable callbacks — only change when tabStack changes (tabs added/removed),
  // NOT on every active-tab switch, so TabItem memo comparisons stay valid.
  const deleteTab = useCallback((item, isSubTab = false) => {
    // INT-10976 scenario 3: closing the Create Bonus V2 tab with the X icon
    // ends that flow for good — the parked wizard draft (INT-10948) and the
    // route's saved scroll position must not resurrect in the next tab.
    // In-flow navigation keeps both; only the explicit close clears them.
    if (item?.path === "/createbonusv2") {
      sessionStorage.removeItem("createbonusv2:draft");
      sessionStorage.removeItem("mainScrollPos:/createbonusv2");
    }
    dispatch(tabManager(item.subtitle, tabStack, true, history, isSubTab));
  }, [dispatch, tabStack, history]);

  const handleChangeTab = useCallback((item) => {
    dispatch(setIsPlayerExist(item.subtitle));
    dispatch(tabManager(item.subtitle, tabStack));
  }, [dispatch, tabStack]);

  useEffect(() => {
    let mounting = true;

    if (mounting) {
      const path = location.pathname;
      const pathArray = path.split("/");
      const pathName = pathArray.includes("customerid") ? parseInt(pathArray[2]) : path;

      if (Array.isArray(tabStack) && tabStack.length > 0) {
        const tabExist = tabStack.some(tab => tab.path === path);
        if (!tabExist) {
          if (pathArray.includes("customerid")) {
            dispatch(getPlayer(pathName, history));
          } else {
            dispatch(tabManager(pathName, tabStack, false, history));
          }
        } else {
          dispatch(tabManager(pathName, tabStack));
        }
      }
    }

    return () => mounting = false;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // REPORT PAGE PART //

  const undateReportTabs = useCallback((reportPage, reportKey) => {
    const subTitles =
      Array.isArray(sidebarTabs) && sidebarTabs.length > 0
        ? sidebarTabs.map((v) => v.subtitle)
        : [];
    if (subTitles.length > 0 && tableShouldView.length > 0) {
      if (
        !subTitles.includes(reportPage) &&
        tableShouldView.includes(reportKey)
      ) {
        const removeReportTable = tableShouldView.filter(
          (v) => v !== reportKey
        );
        dispatch(setTableShouldView(removeReportTable));
      }
    }
  }, [sidebarTabs, tableShouldView, dispatch]);

  useEffect(() => {
    const reports = [
      "SPORTS OVERVIEW",
      "CUSTOMER OVERVIEW",
      "PAYMENT OVERVIEW",
      "MANUAL ADJUSTMENT",
      "ACTIVE PLAYERS",
      "INDIVIDUAL PLAYER",
      "MULTIPLE DEPOSIT",
    ];
    const reportKeys = [
      "SPORT",
      "CUSTOMER",
      "PAYMNET",
      "MANULAPAYMENT",
      "ACTIVE",
      "INDIVIDUAL",
      "multipleDeposit",
    ];
    reports.forEach((v, i) => undateReportTabs(v, reportKeys[i]));
  }, [sidebarTabs, undateReportTabs]);

  // REPORT PAGE PART //

  return (
    <React.Fragment>
      <div ref={tabBarRef} className='sticky top-16 z-30 bg-background pt-2 flex flex-nowrap items-center gap-1.5 mb-4 pb-2 border-b border-border overflow-x-auto scroll-smooth cursor-grab active:cursor-grabbing'>
        {tabStack.length > 0 &&
          tabStack.map((item, ind) => (
            <TabItem
              key={generateKey(ind)}
              item={item}
              isActive={item.subtitle === activeTab}
              onDelete={deleteTab}
              onChangeTab={handleChangeTab}
            />
          ))}
      </div>
    </React.Fragment>
  );
}

export default NavigationTabs;
